import { useTranslation } from '@rhino-project/core/i18n';
import {
  createColumnHelper,
  SortingState,
  useTable
} from '@tanstack/react-table';
import { cloneElement, isValidElement, ReactElement, useMemo } from 'react';

import { Skeleton } from '@heroui/react';

import {
  useGlobalComponentForModel,
  usePaths
} from '@rhino-project/core/hooks';
import { RhinoAttribute, RhinoResource } from '@rhino-project/core';

import { filter, isString } from 'lodash-es';
import { useModelIndexContext } from '@rhino-project/core/hooks';
import { isIdentifier } from '@rhino-project/core/utils';
import { ColumnDisplayConfig, Table } from '../table/Table';
import { rhinoTableFeatures } from '../table/features';
import { ModelCell } from './ModelCell';
import { ModelFooter } from './ModelFooter';
import { ModelHeader } from './ModelHeader';
import { useLocation } from '@tanstack/react-router';

const getViewablePaths = (model: RhinoResource) =>
  filter(model.properties, (a: RhinoAttribute) => {
    return (
      !isIdentifier(a) &&
      a.name !== model.ownedBy &&
      a.type !== 'array' &&
      // jsonb is not a Rhino attribute type, but the runtime schema may emit it
      (a.type as string) !== 'jsonb' &&
      a.type !== 'text' &&
      // a.type !== 'reference' &&
      !a.name.endsWith('_attachment')
    );
  }).map((a: RhinoAttribute) => a.name);

const isDesc = (order: string) => order?.charAt(0) === '-';

const getSortableAttributes = (model: RhinoResource) =>
  filter(
    model.properties,
    (a: RhinoAttribute) =>
      a.type === 'string' ||
      // datetime is a runtime format string here, not a base attribute type
      (a.type as string) === 'datetime' ||
      a.type === 'number' ||
      a.type === 'integer'
  );

// A path is either an attribute path string or a column-defining React element.
// Callers commonly build the list with conditional entries then `.filter(Boolean)`,
// so the prop tolerates falsy placeholders.
type PathInput = string | ReactElement;

export interface ModelIndexTableProps {
  paths?: (PathInput | null | false)[];
  sortPaths?: string[];
  // Relative width and responsive visibility per column, keyed by the
  // column id (the path string, or a column element's id/path prop)
  columnConfig?: Record<string, ColumnDisplayConfig>;
  rowLinkBase?: string;
  onRowClick?: ((...args: unknown[]) => unknown) | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  [key: string]: unknown;
}

export const ModelIndexTableBase = (props: ModelIndexTableProps) => {
  const { t } = useTranslation();
  const { isLoading, limit, model, order, resources, results, setOrder } =
    useModelIndexContext();
  const { paths, sortPaths, columnConfig, rowLinkBase, onRowClick } = props;
  const sorting = useMemo<SortingState>(
    () =>
      order.map((term: string) => ({
        id: term.replace('-', ''),
        desc: isDesc(term)
      })),
    [order]
  );

  const pathsOrDefault = useMemo(() => {
    if (props.overrides?.ModelTable?.props?.paths)
      console.warn('ModelTable pass legacy paths prop');

    return (
      paths ||
      // Legacy
      props.overrides?.ModelTable?.props?.paths ||
      getViewablePaths(model)
    );
  }, [paths, props.overrides?.ModelTable?.props?.paths, model]);

  const computedPaths = usePaths(pathsOrDefault, resources);

  const sortable = useMemo(
    () => sortPaths || getSortableAttributes(model).map((a) => a.name),
    [sortPaths, model]
  );

  const columns = useMemo(
    () =>
      (computedPaths as PathInput[]).map((path, idx) => {
        if (isValidElement(path)) {
          // A column-defining element carries its config on props, whose shape
          // is dynamic (accessor/path/id/header/footer).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const elementProps = path.props as any;
          const accessor =
            elementProps?.accessor ||
            (isString(elementProps?.path) ? elementProps?.path : null);
          // Prefix index-based IDs to avoid colliding with React Aria's
          // internal numeric collection keys
          const id = elementProps?.id || elementProps?.path || `col-${idx}`;
          const header =
            elementProps?.header ||
            (() => (
              <ModelHeader
                model={model}
                path={(elementProps?.path || null) as string}
              />
            ));
          // eslint-disable-next-line @eslint-react/no-clone-element, @typescript-eslint/no-explicit-any
          const cell = (props: any) => cloneElement(path, { model, ...props });
          const footer =
            elementProps?.footer ||
            (() => (
              <ModelFooter
                model={model}
                path={(elementProps?.path || null) as string}
              />
            ));

          if (accessor) {
            return columnHelper.accessor(accessor, {
              id,
              header,
              cell,
              footer,
              enableSorting: sortable.includes(id),
              enableMultiSort: sortable.includes(id),
              meta: columnConfig?.[id]
            });
          }

          return columnHelper.display({
            id,
            header,
            cell,
            footer,
            enableSorting: false,
            meta: columnConfig?.[id]
          });
        }

        // Path is a string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cell = (info: any) =>
          isLoading ? (
            <Skeleton className="h-4 w-1/2" />
          ) : (
            <ModelCell model={model} path={path} {...info} />
          );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const header = (info: any) => (
          <ModelHeader model={model} path={path} {...info} />
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const footer = (info: any) => (
          <ModelFooter model={model} path={path} {...info} />
        );

        return columnHelper.accessor(path, {
          id: path,
          header,
          cell,
          footer,
          enableSorting: sortable.includes(path),
          enableMultiSort: sortable.includes(path),
          meta: columnConfig?.[path]
        });
      }),

    [computedPaths, columnConfig, isLoading, model, sortable]
  );

  const data = useMemo(() => {
    return results || Array(limit).fill({});
  }, [limit, results]);

  const location = useLocation();

  const table = useTable({
    features: rhinoTableFeatures,
    data,
    columns,
    enableMultiSort: true,
    enableSortingRemoval: false,
    manualSorting: true,
    meta: {
      getRowProps: (row) =>
        onRowClick === null
          ? {}
          : { href: `${rowLinkBase ?? location.pathname}/${row.original.id}` }
    },
    state: {
      sorting
    },
    // The controller owns sorting, including external resets. Keeping a
    // second local state lets its effect overwrite a cleared controller order.
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setOrder(next.map((term) => (term.desc ? `-${term.id}` : term.id)));
    }
  });

  return (
    <Table
      table={table}
      ariaLabel={t(`rhino.generated.${model.model}.one`, {
        defaultValue: model.readableName || model.model
      })}
      {...props}
    />
  );
};

// Columns are built dynamically from runtime paths/records.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columnHelper = createColumnHelper<typeof rhinoTableFeatures, any>();

export const ModelIndexTable = (props: ModelIndexTableProps) =>
  useGlobalComponentForModel('ModelIndexTable', ModelIndexTableBase, props);
